import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Chip,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import { Order } from '@barboxx/models';

import { BaseTextInput } from './BaseTextInput';

function getReferenceSourceLabel(
    diagnostics?: Order.PickupFaceVerificationDiagnostics | null
): string {
    const matchedReferencePose = diagnostics?.summary.matchedReferencePose;

    if (matchedReferencePose === 'center') {
        return 'Center';
    }

    if (matchedReferencePose === 'left') {
        return 'Left';
    }

    if (matchedReferencePose === 'right') {
        return 'Right';
    }

    if (matchedReferencePose === 'unknown') {
        return 'Unknown';
    }

    const path = diagnostics?.summary.matchedReferencePath;

    if (!path) {
        return 'Center';
    }

    if (path.includes('/center') || path.includes('center')) {
        return 'Center';
    }

    if (path.includes('/left') || path.includes('left')) {
        return 'Left (legacy)';
    }

    if (path.includes('/right') || path.includes('right')) {
        return 'Right (legacy)';
    }

    return path.split('/').pop() || path;
}

function getReferenceSourcePath(
    diagnostics?: Order.PickupFaceVerificationDiagnostics | null
): string {
    return diagnostics?.summary.matchedReferencePath || 'Not stored';
}

export function FaceVerificationDiagnosticsSection({
    diagnostics,
}: {
    diagnostics: Order.PickupFaceVerificationDiagnostics;
}) {
    return (
        <Stack gap={2}>
            <Typography variant="subtitle2">Diagnostics</Typography>
            <Stack direction="row" gap={2} flexWrap="wrap">
                <BaseTextInput
                    label="Decision"
                    value={diagnostics.summary.decisionOutcome}
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Risk Score"
                    value={diagnostics.summary.riskScore}
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Attempt"
                    value={diagnostics.summary.attemptCount}
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Escalated"
                    value={
                        diagnostics.summary.escalatedToBartender ? 'Yes' : 'No'
                    }
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Source Faces"
                    value={diagnostics.summary.sourceFaceCount}
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Reference Faces"
                    value={diagnostics.summary.referenceFaceCount}
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Reference Source"
                    value={getReferenceSourceLabel(diagnostics)}
                    sx={{ minWidth: 180 }}
                />
                <BaseTextInput
                    label="Reference Path"
                    value={getReferenceSourcePath(diagnostics)}
                    sx={{ minWidth: 320 }}
                />
                <BaseTextInput
                    label="Multiple Faces"
                    value={
                        diagnostics.summary.hasMultipleSourceFaces
                            ? 'Yes'
                            : 'No'
                    }
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Too Dark"
                    value={diagnostics.summary.likelyTooDark ? 'Yes' : 'No'}
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Blurry"
                    value={diagnostics.summary.likelyBlurry ? 'Yes' : 'No'}
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Occluded"
                    value={diagnostics.summary.likelyOccluded ? 'Yes' : 'No'}
                    sx={{ minWidth: 140 }}
                />
                <BaseTextInput
                    label="Partial Face"
                    value={diagnostics.summary.likelyPartialFace ? 'Yes' : 'No'}
                    sx={{ minWidth: 140 }}
                />
            </Stack>
            {(diagnostics.summary.reasonCodes?.length ?? 0) > 0 ? (
                <Stack direction="row" gap={1} flexWrap="wrap">
                    {diagnostics.summary.reasonCodes?.map(reasonCode => (
                        <Chip
                            key={reasonCode}
                            label={reasonCode}
                            size="small"
                            variant="outlined"
                        />
                    ))}
                </Stack>
            ) : null}

            <Accordion disableGutters>
                <AccordionSummary>
                    <Typography variant="body2">
                        Advanced Diagnostics JSON
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TextField
                        label="Diagnostics JSON"
                        value={JSON.stringify(diagnostics, null, 2)}
                        multiline
                        minRows={10}
                        slotProps={{
                            input: {
                                readOnly: true,
                            },
                        }}
                        sx={{
                            width: '100%',
                            fontFamily: 'monospace',
                        }}
                    />
                </AccordionDetails>
            </Accordion>
        </Stack>
    );
}
